/**
 * CenterDetailPage - 센터 상세 페이지
 *
 * /centers/:centerId 경로로 접근하며, 특정 센터의 상세 정보와
 * 센터 관리 진입점(강사 매핑 포함), 수업 일지 작성 시작일 설정을 제공한다.
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useCenters } from "@/hooks/useCenters";
import { useCurrentUser } from "@/components/AuthProvider";
import { EditCenterModal } from "@/components/EditCenterModal";
import { AdminSection } from "@/components/AdminSection";
import { useResetCenter } from "@/hooks/useResetCenter";
import { getBranchPolicy, saveBranchPolicy } from "@/services/firestore/branch-policy";

/** 날짜 문자열을 "YYYY.MM.DD" 형식으로 변환한다. */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
}

export function CenterDetailPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const user = useCurrentUser();
  const { data, isLoading, isError, error, refetch } = useCenters();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetConfirmName, setResetConfirmName] = useState("");
  const [resetError, setResetError] = useState<string | null>(null);
  const resetMutation = useResetCenter();

  // 수업 일지 작성 시작일 상태
  const [journalStartDate, setJournalStartDate] = useState("");
  const [startDateLoading, setStartDateLoading] = useState(false);
  const [startDateSaving, setStartDateSaving] = useState(false);
  const [startDateSaveSuccess, setStartDateSaveSuccess] = useState(false);
  const [startDateError, setStartDateError] = useState<string | null>(null);

  // BranchPolicy에서 시작일 로드
  useEffect(() => {
    if (!user || !centerId) return;
    let cancelled = false;
    setStartDateLoading(true);
    getBranchPolicy(user.uid, Number(centerId))
      .then((policy) => {
        if (!cancelled) {
          setJournalStartDate(policy.journalWritingStartDate ?? "");
        }
      })
      .catch(() => {
        // 실패 시 빈 상태 유지
      })
      .finally(() => {
        if (!cancelled) setStartDateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, centerId]);

  const handleSaveStartDate = async () => {
    if (!user || !centerId) return;
    setStartDateSaving(true);
    setStartDateSaveSuccess(false);
    setStartDateError(null);
    try {
      const policy = await getBranchPolicy(user.uid, Number(centerId));
      await saveBranchPolicy(user.uid, Number(centerId), {
        ...policy,
        journalWritingStartDate: journalStartDate || undefined,
      });
      setStartDateSaveSuccess(true);
    } catch (err: unknown) {
      setStartDateError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    } finally {
      setStartDateSaving(false);
    }
  };

  const handleClearStartDate = async () => {
    if (!user || !centerId) return;
    setStartDateSaving(true);
    setStartDateSaveSuccess(false);
    setStartDateError(null);
    try {
      const policy = await getBranchPolicy(user.uid, Number(centerId));
      await saveBranchPolicy(user.uid, Number(centerId), {
        ...policy,
        journalWritingStartDate: undefined,
      });
      setJournalStartDate("");
      setStartDateSaveSuccess(true);
    } catch (err: unknown) {
      setStartDateError(err instanceof Error ? err.message : "삭제에 실패했습니다.");
    } finally {
      setStartDateSaving(false);
    }
  };

  const centers = data?.centers ?? [];
  const center = centers.find((c) => c.centerId === Number(centerId));

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/centers" className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 목록
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/centers" className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 목록
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">
            {error instanceof Error ? error.message : "데이터를 불러오는 중 오류가 발생했습니다."}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // --- 센터를 찾을 수 없는 상태 ---
  if (!center) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/centers" className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 목록
          </Link>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-yellow-800">센터를 찾을 수 없습니다</h2>
          <p className="text-yellow-700 mt-1">요청하신 센터가 존재하지 않거나 삭제되었습니다.</p>
          <Link
            to="/centers"
            className="inline-block mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            센터 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  // --- 정상 상태: 센터 상세 + Studiomate 연동 ---
  return (
    <div className="space-y-6">
      {/* 헤더 섹션 */}
      <div>
        <Link to="/centers" className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 센터 목록
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{center.name}</h1>
            {center.location && <p className="mt-2 text-sm text-gray-600">{center.location}</p>}
            {center.contact && <p className="mt-1 text-sm text-gray-600">{center.contact}</p>}
            <p className="mt-1 text-sm text-gray-400">생성일: {formatDate(center.createdAt)}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
            >
              수정
            </button>
            <button
              onClick={() => {
                setIsResetModalOpen(true);
                setResetConfirmName("");
                setResetError(null);
              }}
              className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 센터 정보 수정 모달 */}
      <EditCenterModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onUpdated={() => {
          setIsEditModalOpen(false);
          refetch();
        }}
        center={center}
      />

      {/* 센터 초기화 확인 모달 */}
      {isResetModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">센터 초기화</h3>
            <p className="text-gray-600 mt-2">
              센터의 모든 데이터(강사, 회원, 수업, 정산, Studiomate 연동 등)가 삭제되고 기본값으로
              초기화됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                확인을 위해 센터 이름을 입력해주세요
              </label>
              <input
                type="text"
                value={resetConfirmName}
                onChange={(e) => setResetConfirmName(e.target.value)}
                placeholder={center.name}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {resetError && <p className="mt-2 text-sm text-red-600">{resetError}</p>}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsResetModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                취소
              </button>
              <button
                onClick={async () => {
                  try {
                    setResetError(null);
                    await resetMutation.mutateAsync({
                      centerId: center.centerId,
                    });
                    setIsResetModalOpen(false);
                    refetch();
                  } catch (err: unknown) {
                    setResetError(err instanceof Error ? err.message : "초기화에 실패했습니다.");
                  }
                }}
                disabled={resetConfirmName !== center.name || resetMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetMutation.isPending ? "처리 중..." : "초기화"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 센터 관리 링크 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800">센터 관리</h2>
        <div className="mt-4 flex gap-4">
          <Link
            to={`/centers/${centerId}/settings`}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            지점 정책 설정
          </Link>
          <Link
            to={`/centers/${centerId}/members`}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            회원 관리
          </Link>
          <Link
            to={`/centers/${centerId}/ticket-catalog`}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            수강권 관리
          </Link>
          <Link
            to={`/centers/${centerId}/schedules`}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            수업 관리
          </Link>
          <Link
            to={`/centers/${centerId}/instructors`}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
          >
            강사 관리
          </Link>
        </div>
      </div>

      {/* 관리자 관리 섹션 */}
      <AdminSection centerId={center.centerId} centerName={center.name} />

      {/* 수업 일지 작성 시작일 설정 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800">수업 일지 작성 시작일</h2>
        <p className="text-sm text-gray-500 mt-1">
          설정한 날짜 이전의 수업은 강사 앱에서 조회/일지작성/정산이 불가합니다.
        </p>

        {startDateLoading ? (
          <p className="mt-4 text-sm text-gray-400">로딩 중...</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-4">
              <input
                type="date"
                value={journalStartDate}
                onChange={(e) => {
                  setJournalStartDate(e.target.value);
                  setStartDateSaveSuccess(false);
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={() => void handleSaveStartDate()}
                disabled={startDateSaving || !journalStartDate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {startDateSaving ? "저장 중..." : "저장"}
              </button>
              {journalStartDate && (
                <button
                  onClick={() => void handleClearStartDate()}
                  disabled={startDateSaving}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  삭제
                </button>
              )}
            </div>

            {startDateSaveSuccess && <p className="text-sm text-green-600">저장되었습니다.</p>}
            {startDateError && <p className="text-sm text-red-600">{startDateError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
